import ResponsiveTable from "../tables/ResponsiveTable.jsx";
import React, { useState, useEffect, useCallback } from 'react'
import { api } from '../../utils/ihthisabi/api'
import { useLocation as useHierarchyLocation } from '../../hooks/useLocation'
import UnitAdminProfileModal from './UnitAdminProfileModal'
import UserProfileModal from './UserProfileModal'
import ConfirmationModal from './ConfirmationModal'
import MemberFormModal, { LocationSelects } from './MemberFormModal'
import { MemberCategoryBadge } from './MemberCategoryControl'
import Pagination from './Pagination'
import {
  Upload,
  UserPlus,
  Users,
  Search,
  Filter,
  Trash2,
  Download,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  XCircle,
  Eye,
  Edit,
  Settings,
  ArrowRightLeft,
  Plus,
  X
} from 'lucide-react'
import toast from 'react-hot-toast'

const UserManagement = () => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [districtFilter, setDistrictFilter] = useState('')
  const [areaFilter, setAreaFilter] = useState('')
  const [unitFilter, setUnitFilter] = useState('')
  const [selectedDistrictId, setSelectedDistrictId] = useState('')
  const [selectedAreaId, setSelectedAreaId] = useState('')
  const [selectedUnitId, setSelectedUnitId] = useState('')
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0
  })
  const [meta, setMeta] = useState({ totalUsers: 0, activeUsers: 0, units: [] })
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadResult, setUploadResult] = useState(null)
  const [templateLoading, setTemplateLoading] = useState(false)
  const [showAddMemberModal, setShowAddMemberModal] = useState(false)
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false)
  const [deleteAllLoading, setDeleteAllLoading] = useState(false)
  const [showUnitAdminsOnly, setShowUnitAdminsOnly] = useState(false)
  const [unitAdmins, setUnitAdmins] = useState([])
  const [unitAdminPagination, setUnitAdminPagination] = useState({
    current: 1,
    pages: 1,
    total: 0
  })
  const [selectedUnitAdminId, setSelectedUnitAdminId] = useState(null)
  const [showProfileModal, setShowProfileModal] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [showUserProfileModal, setShowUserProfileModal] = useState(false)
  const [deleteUserModal, setDeleteUserModal] = useState({ isOpen: false, userId: null, userName: null })
  // Transfer modal state
  const [transferModal, setTransferModal] = useState({ isOpen: false, user: null })
  const [transferMode, setTransferMode] = useState('location') // 'location' | 'abroad'
  const [transferLoc, setTransferLoc] = useState({ district: '', area: '', unit: '' })
  const [abroadCountries, setAbroadCountries] = useState([])
  const [abroadAreas, setAbroadAreas] = useState([])
  const [abroadUnits, setAbroadUnits] = useState([])
  const [transferAbroadCountry, setTransferAbroadCountry] = useState('')
  const [transferAbroadArea, setTransferAbroadArea] = useState('')
  const [transferAbroadUnit, setTransferAbroadUnit] = useState('')
  const [transferWorking, setTransferWorking] = useState(false)
  const [transferError, setTransferError] = useState('')
  const {
    districts,
    areas,
    units,
    loading: locationLoading,
    onDistrictChange,
    onAreaChange
  } = useHierarchyLocation()

  const getLocationOptionId = (option) => option?.id || option?._id || option?.code || option?.name || option?.title || ''
  const getLocationOptionLabel = (option) => option?.name || option?.title || option?.label || option?.id || option?.code || ''

  // Send the full selected path — area/unit names repeat across districts
  const appendLocationFilter = useCallback((params) => {
    if (districtFilter) params.append('district', districtFilter)
    if (areaFilter) params.append('area', areaFilter)
    if (unitFilter) params.append('unit', unitFilter)
  }, [districtFilter, areaFilter, unitFilter])

  // Fetch users
  const fetchUsers = useCallback(async (page = 1) => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10'
      })

      if (searchTerm) params.append('search', searchTerm)
      appendLocationFilter(params)

      const response = await api.get(`/ihthisabi/admin/users?${params}`)
      console.log('Users response:', response.data.data)
      setUsers(response.data.data.users)
      setPagination(response.data.data.pagination)
    } catch (error) {
      console.error('Error fetching users:', error)
      toast.error('Failed to fetch users')
    } finally {
      setLoading(false)
    }
  }, [searchTerm, appendLocationFilter])

  const fetchMeta = useCallback(async () => {
    try {
      const response = await api.get('/ihthisabi/admin/users/meta')
      setMeta(response.data.data)
    } catch (error) {
      console.error('Error fetching users meta:', error)
    }
  }, [])

  useEffect(() => {
    fetchMeta()
  }, [fetchMeta])

  // Download the model Excel so admins fill in exactly the columns the parser expects
  const handleDownloadTemplate = async () => {
    setTemplateLoading(true)
    try {
      const response = await api.get('/ihthisabi/admin/members-template', { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'member-upload-template.xlsx')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Template download error:', error)
      toast.error('Failed to download the model Excel')
    } finally {
      setTemplateLoading(false)
    }
  }

  // Handle file upload
  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to upload')
      return
    }

    setUploadLoading(true)
    const formData = new FormData()
    formData.append('excelFile', selectedFile)

    try {
      const response = await api.post('/ihthisabi/admin/upload-excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 90000 // allow up to 60s for large Excel processing
      })

      // Keep the modal open — the result summary (including skipped rows) renders inside it
      setUploadResult(response.data.data)
      toast.success('Excel file processed successfully!')
      setSelectedFile(null)
      fetchUsers() // Refresh users list
      fetchMeta()
    } catch (error) {
      console.error('Upload error:', error)
      setUploadResult(error.response?.data?.data || null)
      toast.error(error.response?.data?.message || 'Failed to upload file')
    } finally {
      setUploadLoading(false)
    }
  }

  // Delete user
  const handleDeleteUser = (e, userId, userName) => {
    e.stopPropagation() // Prevent row click event
    setDeleteUserModal({ isOpen: true, userId, userName })
  }

  const confirmDeleteUser = async () => {
    try {
      await api.delete(`/ihthisabi/admin/users/${deleteUserModal.userId}`)
      toast.success('User deleted successfully')
      fetchUsers(pagination.current)
      setDeleteUserModal({ isOpen: false, userId: null, userName: null })
    } catch (error) {
      console.error('Delete error:', error)
      toast.error('Failed to delete user')
    }
  }

  // Transfer handlers
  const openTransferModal = async (e, user) => {
    e.stopPropagation()
    setTransferError('')
    setTransferMode('location')
    setTransferLoc({ district: '', area: '', unit: '' })
    setTransferAbroadCountry(''); setTransferAbroadArea(''); setTransferAbroadUnit('')
    setAbroadAreas([]); setAbroadUnits([])
    setTransferModal({ isOpen: true, user })
    // Fetch abroad countries
    try {
      const countryRes = await api.get('/ihthisabi/admin/abroad-countries')
      setAbroadCountries(countryRes.data.data?.countries || [])
    } catch (err) {
      setTransferError(err.response?.data?.message || 'Failed to load locations')
    }
  }

  const handleTransferAbroadCountryChange = async (countryId) => {
    setTransferAbroadCountry(countryId); setTransferAbroadArea(''); setTransferAbroadUnit('')
    setAbroadAreas([]); setAbroadUnits([])
    if (!countryId) return
    try {
      const res = await api.get('/ihthisabi/admin/abroad-areas', { params: { country: countryId } })
      setAbroadAreas(res.data.data?.areas || [])
    } catch { /* ignore */ }
  }

  const handleTransferAbroadAreaChange = async (areaId) => {
    setTransferAbroadArea(areaId); setTransferAbroadUnit(''); setAbroadUnits([])
    if (!areaId) return
    try {
      const res = await api.get('/ihthisabi/admin/abroad-units', { params: { area: areaId } })
      setAbroadUnits(res.data.data?.units || [])
    } catch { /* ignore */ }
  }

  const confirmTransfer = async () => {
    setTransferError('')
    const { user } = transferModal
    if (transferMode === 'abroad') {
      if (!transferAbroadCountry) return setTransferError('Please select a country')
      setTransferWorking(true)
      try {
        await api.put(`/ihthisabi/admin/users/${user._id}/transfer`, {
          isAbroad: true,
          abroadCountry: transferAbroadCountry,
          abroadArea: transferAbroadArea || null,
          abroadUnit: transferAbroadUnit || null
        })
        toast.success('User transferred to abroad')
        setTransferModal({ isOpen: false, user: null })
        fetchUsers(pagination.current)
      } catch (e) { setTransferError(e.response?.data?.message || 'Transfer failed') }
      setTransferWorking(false)
    } else {
      if (!transferLoc.district || !transferLoc.area || !transferLoc.unit) return setTransferError('Please select district, area and unit')
      setTransferWorking(true)
      try {
        await api.put(`/ihthisabi/admin/users/${user._id}/transfer`, {
          isAbroad: false, district: transferLoc.district, area: transferLoc.area, unit: transferLoc.unit
        })
        toast.success('User transferred successfully')
        setTransferModal({ isOpen: false, user: null })
        fetchUsers(pagination.current)
      } catch (e) { setTransferError(e.response?.data?.message || 'Transfer failed') }
      setTransferWorking(false)
    }
  }

  // Reset filters
  const resetFilters = () => {
    setSearchTerm('')
    setDistrictFilter('')
    setAreaFilter('')
    setUnitFilter('')
    setSelectedDistrictId('')
    setSelectedAreaId('')
    setSelectedUnitId('')
    onDistrictChange('')
  }

  const handleDistrictFilterChange = (districtId) => {
    setSelectedDistrictId(districtId)

    const selectedDistrict = districts.find((district) => getLocationOptionId(district) === districtId)
    setDistrictFilter(selectedDistrict ? getLocationOptionLabel(selectedDistrict) : '')

    setSelectedAreaId('')
    setAreaFilter('')
    setSelectedUnitId('')
    setUnitFilter('')
    onDistrictChange(districtId)
  }

  const handleAreaFilterChange = (areaId) => {
    setSelectedAreaId(areaId)

    const selectedArea = areas.find((area) => getLocationOptionId(area) === areaId)
    setAreaFilter(selectedArea ? getLocationOptionLabel(selectedArea) : '')

    setSelectedUnitId('')
    setUnitFilter('')
    onAreaChange(areaId)
  }

  const handleUnitFilterChange = (unitId) => {
    setSelectedUnitId(unitId)
    const selectedUnit = units.find((unit) => getLocationOptionId(unit) === unitId)
    setUnitFilter(selectedUnit ? getLocationOptionLabel(selectedUnit) : '')
  }

  // Fetch unit admins
  const fetchUnitAdmins = useCallback(async (page = 1) => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10'
      })

      if (searchTerm) params.append('search', searchTerm)
      appendLocationFilter(params)

      const response = await api.get(`/ihthisabi/admin/unitadmins?${params}`)
      console.log('Unit admins response:', response.data.data)
      setUnitAdmins(response.data.data.unitAdmins || [])
      setUnitAdminPagination(response.data.data.pagination || { current: 1, pages: 1, total: 0 })
    } catch (error) {
      console.error('Error fetching unit admins:', error)
      toast.error('Failed to fetch unit admins')
    } finally {
      setLoading(false)
    }
  }, [searchTerm, appendLocationFilter])

  useEffect(() => {
    if (showUnitAdminsOnly) {
      fetchUnitAdmins()
    } else {
      fetchUsers()
    }
  }, [showUnitAdminsOnly, fetchUnitAdmins, fetchUsers])

  // Delete all users
  const handleDeleteAllUsers = async () => {
    setDeleteAllLoading(true)
    try {
      const response = await api.delete('/ihthisabi/admin/delete-all-users')
      toast.success(`Successfully deleted ${response.data.data.deletedCount} users`)
      setShowDeleteAllModal(false)
      fetchUsers() // Refresh users list
      // Also refresh meta data
      const metaResponse = await api.get('/ihthisabi/admin/users/meta')
      setMeta(metaResponse.data.data)
    } catch (error) {
      console.error('Delete all users error:', error)
      toast.error('Failed to delete all users')
    } finally {
      setDeleteAllLoading(false)
    }
  }

  const openMobileProfile = (row) => {
    if (showUnitAdminsOnly) {
      setSelectedUnitAdminId(row._id)
      setShowProfileModal(true)
    } else {
      setSelectedUserId(row._id)
      setShowUserProfileModal(true)
    }
  }

  return (
    <div className="space-y-2 sm:space-y-5">
      {!showUnitAdminsOnly && (
        <button
          onClick={() => setShowAddMemberModal(true)}
          title="Add Member" aria-label="Add Member" className="ih-fab"
        >
          <Plus className="h-5 w-5" />
        </button>
      )}
      {/* Header — title hidden on mobile, the app bar already names the page.
          Actions collapse to icons so they stay on one row. */}
      <div className="flex items-center justify-between gap-2">
        <div className="hidden min-w-0 lg:block">
          <h2 className="ih-page-title">User Management</h2>
          <p className="ih-page-subtitle">
            {showUnitAdminsOnly ? 'Manage unit admins' : 'Manage users uploaded from Excel files'}
          </p>
        </div>

        <div className="flex w-full items-center gap-2 lg:w-auto">
          <button
            onClick={() => {
              setShowUnitAdminsOnly(!showUnitAdminsOnly)
              resetFilters()
            }}
            className={`inline-flex h-[44px] min-w-0 flex-1 items-center justify-center gap-1 rounded-full px-3 text-[11px] font-medium transition-colors sm:h-9 sm:flex-none sm:px-4 sm:text-sm ${
              showUnitAdminsOnly ? 'bg-primary text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
            style={showUnitAdminsOnly ? undefined : { backgroundColor: 'rgba(16,24,40,0.04)' }}
          >
            <Settings className="h-4 w-4 shrink-0" />
            <span className="truncate">{showUnitAdminsOnly ? 'Regular Users' : 'Unit Admins'}</span>
          </button>

          {(meta.totalUsers > 0 || users.length > 0) && !showUnitAdminsOnly && (
            <button
              onClick={() => setShowDeleteAllModal(true)}
              title="Delete all users"
              className="inline-flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full bg-red-50 text-red-500 transition-colors hover:bg-red-100 sm:h-9 sm:w-9"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          {!showUnitAdminsOnly && (
            <>
              <button
                onClick={() => setShowAddMemberModal(true)}
                className="btn-primary hidden shrink-0 gap-1 px-3 text-[11px] sm:inline-flex sm:h-9 sm:px-4 sm:text-sm"
              >
                <UserPlus className="h-4 w-4" />
                <span>Add Member</span>
              </button>
              <button
                onClick={() => { setUploadResult(null); setShowUploadModal(true) }}
                className="btn-ghost h-[44px] shrink-0 gap-1 rounded-full border border-gray-300 px-3 text-[11px] sm:h-9 sm:px-4 sm:text-sm"
              >
                <Upload className="h-4 w-4" />
                Upload
              </button>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        {(showUnitAdminsOnly
          ? [
              { label: 'Total Unit Admins', short: 'Total', value: unitAdminPagination.total || 0, Icon: Settings, tone: 'bg-blue-50 text-blue-600' },
              { label: 'Active Unit Admins', short: 'Active', value: unitAdmins.filter(u => u.isActive).length, Icon: CheckCircle, tone: 'bg-green-50 text-green-600' },
              { label: 'Inactive Unit Admins', short: 'Inactive', value: unitAdmins.filter(u => !u.isActive).length, Icon: AlertCircle, tone: 'bg-amber-50 text-amber-600' },
            ]
          : [
              { label: 'Total Users', short: 'Users', value: meta.totalUsers || pagination.total || 0, Icon: Users, tone: 'bg-blue-50 text-blue-600' },
              { label: 'Active Users', short: 'Active', value: meta.activeUsers || users.filter(u => u.isActive).length, Icon: CheckCircle, tone: 'bg-green-50 text-green-600' },
              { label: 'Total Units', short: 'Units', value: meta.units?.length || 0, Icon: AlertCircle, tone: 'bg-amber-50 text-amber-600' },
            ]
        ).map(({ label, short, value, Icon, tone }) => (
          <div key={label} className="ih-stat-card">
            <div className="flex items-start justify-between gap-1.5">
              <div className="min-w-0">
                <p className="ih-stat-label truncate">
                  <span className="sm:hidden">{short}</span>
                  <span className="hidden sm:inline">{label}</span>
                </p>
                <p className="ih-stat-value mt-1">{value}</p>
              </div>
              <div className={`ih-stat-icon ${tone} hidden sm:flex`}>
                <Icon className="w-4 h-4" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      {/* Filters — placeholders carry the labels so no label rows are needed. */}
      <div className="ih-surface p-2 sm:p-3">
        <div className="flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <Search className="ih-filter-icon" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search name or RUKN ID..."
              className="ih-field h-[44px] pr-3 text-base sm:h-9 sm:text-sm"
            />
          </div>
          {(searchTerm || districtFilter || areaFilter || unitFilter) && (
            <button
              onClick={resetFilters}
              className="inline-flex h-[44px] shrink-0 items-center rounded-full px-3 text-[11px] font-medium text-gray-500 transition-colors hover:text-gray-800 sm:h-9"
            >
              Reset
            </button>
          )}
        </div>

        <div className="mt-2 grid grid-cols-3 gap-2">
          <select
            value={selectedDistrictId}
            onChange={(e) => handleDistrictFilterChange(e.target.value)}
            className="ih-filter-select pl-2"
          >
            <option value="">All Districts</option>
            {districts.map((district) => (
              <option key={getLocationOptionId(district)} value={getLocationOptionId(district)}>
                {getLocationOptionLabel(district)}
              </option>
            ))}
          </select>

          <select
            value={selectedAreaId}
            onChange={(e) => handleAreaFilterChange(e.target.value)}
            className="ih-filter-select pl-2 disabled:bg-gray-50 disabled:text-gray-400"
            disabled={!selectedDistrictId || locationLoading.areas}
          >
            <option value="">All Areas</option>
            {areas.map((area) => (
              <option key={getLocationOptionId(area)} value={getLocationOptionId(area)}>
                {getLocationOptionLabel(area)}
              </option>
            ))}
          </select>

          <select
            value={selectedUnitId}
            onChange={(e) => handleUnitFilterChange(e.target.value)}
            className="ih-filter-select pl-2 disabled:bg-gray-50 disabled:text-gray-400"
            disabled={!selectedAreaId || locationLoading.units}
          >
            <option value="">All Units</option>
            {units.map((unit) => (
              <option key={getLocationOptionId(unit)} value={getLocationOptionId(unit)}>
                {getLocationOptionLabel(unit)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="ih-surface overflow-hidden">
        <div className="flex items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-3 py-1.5">
          <h3 className="text-[11px] font-semibold text-gray-700 sm:text-sm">Users List</h3>
          <div className="truncate text-[10px] text-gray-500 sm:text-xs">
            {loading ? 'Loading...' : (
              <>
                {showUnitAdminsOnly ? unitAdmins.length : users.length} of {showUnitAdminsOnly ? (unitAdminPagination.total || 0) : (pagination.total || meta.totalUsers || 0)}
                {(searchTerm || districtFilter || areaFilter || unitFilter) && (
                  <span className="ml-1 text-blue-600">(filtered)</span>
                )}
              </>
            )}
          </div>
        </div>

        {/* Seven columns cannot stay legible at phone widths, so mobile gets a
            card list with just identity, location, status and actions. */}
        <div className="hidden lg:block lg:overflow-x-auto">
          <ResponsiveTable className="ih-table-compact w-full min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  RUKN ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                {!showUnitAdminsOnly && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gender
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  District
                </th>
                {showUnitAdminsOnly && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                {!showUnitAdminsOnly && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={showUnitAdminsOnly ? '6' : '7'} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mr-3"></div>
                      Loading {showUnitAdminsOnly ? 'unit admins' : 'users'}...
                    </div>
                  </td>
                </tr>
              ) : showUnitAdminsOnly ? (
                unitAdmins.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      No unit admins found
                    </td>
                  </tr>
                ) : (
                  unitAdmins.map((admin) => (
                    <tr
                      key={admin._id}
                      onClick={() => {
                        setSelectedUnitAdminId(admin._id)
                        setShowProfileModal(true)
                      }}
                      className="cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {admin.ruknId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {admin.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {admin.unit}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {admin.district || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {admin.contactNo || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          admin.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {admin.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))
                )
              ) : (
                users.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr
                      key={user._id}
                      onClick={() => {
                        setSelectedUserId(user._id)
                        setShowUserProfileModal(true)
                      }}
                      className="cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {user.ruknId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="inline-flex items-center gap-2">
                          {user.name}
                          <MemberCategoryBadge category={user.memberCategory} />
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.gender}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.unit}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.district || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => openTransferModal(e, user)}
                          className="ih-action ih-action-neutral mr-1"
                          title="Transfer user"
                        >
                          <ArrowRightLeft className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => handleDeleteUser(e, user._id, user.name)}
                          title="Delete user"
                          className="ih-action ih-action-delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )
              )}
            </tbody>
          </ResponsiveTable>
        </div>

        {/* Mobile list */}
        <div className="ih-list lg:hidden">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-xs text-gray-500">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              Loading {showUnitAdminsOnly ? 'unit admins' : 'users'}...
            </div>
          ) : (showUnitAdminsOnly ? unitAdmins : users).length === 0 ? (
            <div className="py-8 text-center text-xs text-gray-500">
              No {showUnitAdminsOnly ? 'unit admins' : 'users'} found
            </div>
          ) : (
            (showUnitAdminsOnly ? unitAdmins : users).map((row) => (
              <div
                key={row._id}
                role="button"
                tabIndex={0}
                onClick={() => openMobileProfile(row)}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) return
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    openMobileProfile(row)
                  }
                }}
                aria-label={`View ${showUnitAdminsOnly ? 'unit admin' : 'user'} ${row.name || row.ruknId || ''}`}
                className="ih-list-row cursor-pointer rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
              >
                <div className="ih-avatar bg-primary/10 text-primary">
                  {(row.name || 'U').charAt(0).toUpperCase()}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <div className="ih-list-title min-w-0 max-w-full">{row.name}</div>
                    <MemberCategoryBadge category={row.memberCategory} />
                  </div>
                  <div className="ih-list-meta">
                    {row.ruknId}
                    {!showUnitAdminsOnly && row.gender ? ` · ${row.gender}` : ''}
                    {showUnitAdminsOnly && row.contactNo ? ` · ${row.contactNo}` : ''}
                  </div>
                  <div className="ih-list-meta">
                    {[row.district, row.unit].filter(Boolean).join(' - ') || '—'}
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  <span className={`ih-chip ${
                    row.isActive
                      ? 'border-green-200 bg-green-100 text-green-800'
                      : 'border-red-200 bg-red-100 text-red-800'
                  }`}>
                    {row.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {!showUnitAdminsOnly && (
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => openTransferModal(e, row)}
                        title="Transfer user"
                        aria-label={`Transfer ${row.name || 'user'}`}
                        className="ih-action ih-action-neutral"
                      >
                        <ArrowRightLeft className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => handleDeleteUser(e, row._id, row.name)}
                        title="Delete user"
                        aria-label={`Delete ${row.name || 'user'}`}
                        className="ih-action ih-action-delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {showUnitAdminsOnly ? (
          <Pagination pagination={unitAdminPagination} onPageChange={fetchUnitAdmins} loading={loading} itemLabel="unit admins" />
        ) : (
          <Pagination pagination={pagination} onPageChange={fetchUsers} loading={loading} itemLabel="users" />
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg my-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Upload Excel File</h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Excel File
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="form-input"
                />
              </div>

              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-sm text-blue-900">
                    <p className="font-medium">Not sure about the format?</p>
                    <p className="mt-0.5 text-blue-700">Download the model Excel and fill your members into it.</p>
                  </div>
                  <button
                    onClick={handleDownloadTemplate}
                    disabled={templateLoading}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                  >
                    <Download className="w-3.5 h-3.5" />
                    {templateLoading ? 'Preparing…' : 'Model Excel'}
                  </button>
                </div>
              </div>

              <div className="text-sm text-gray-600">
                <p className="font-medium mb-2">Expected columns:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>SL NO <span className="text-gray-400">(optional)</span></li>
                  <li>DISTRICT, AREA, UNIT <span className="text-red-500">— required</span></li>
                  <li>RUKN ID <span className="text-red-500">— required, digits only, unique</span></li>
                  <li>RUKN NAME <span className="text-red-500">— required</span></li>
                  <li>GENDER <span className="text-red-500">— required (Male / Female)</span></li>
                  <li>CONTACT NO, EMAIL ID, COUNTRY <span className="text-gray-400">(optional)</span></li>
                </ul>
                <p className="mt-2 text-xs text-gray-500">
                  An existing Rukn ID updates that member instead of creating a duplicate.
                </p>
              </div>

              {uploadResult && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Upload Results</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>Imported rows: {uploadResult.totalProcessed}</li>
                    <li className="text-green-700">Created: {uploadResult.created}</li>
                    <li className="text-green-700">Updated: {uploadResult.updated}</li>
                    {uploadResult.errors?.length > 0 && (
                      <li className="text-red-700">Failed to save: {uploadResult.errors.length}</li>
                    )}
                    {uploadResult.skipped?.length > 0 && (
                      <li className="text-amber-700">Skipped rows: {uploadResult.skipped.length}</li>
                    )}
                  </ul>

                  {uploadResult.skipped?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-amber-800 mb-1">
                        These rows were not imported — fix them and upload again:
                      </p>
                      <div className="max-h-40 overflow-y-auto rounded border border-amber-200 bg-amber-50">
                        <ResponsiveTable className="w-full text-xs">
                          <tbody>
                            {uploadResult.skipped.map((s, i) => (
                              <tr key={`${s.row}-${i}`} className="border-b border-amber-100 last:border-0">
                                <td className="px-2 py-1 text-amber-900 whitespace-nowrap">Row {s.row}</td>
                                <td className="px-2 py-1 text-amber-900">{s.name || s.ruknId || '—'}</td>
                                <td className="px-2 py-1 text-amber-700">{s.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </ResponsiveTable>
                      </div>
                    </div>
                  )}

                  {uploadResult.errors?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-red-800 mb-1">Rows that failed to save:</p>
                      <div className="max-h-40 overflow-y-auto rounded border border-red-200 bg-red-50">
                        <ResponsiveTable className="w-full text-xs">
                          <tbody>
                            {uploadResult.errors.map((err, i) => (
                              <tr key={`${err.ruknId}-${i}`} className="border-b border-red-100 last:border-0">
                                <td className="px-2 py-1 text-red-900 whitespace-nowrap">{err.ruknId}</td>
                                <td className="px-2 py-1 text-red-900">{err.name}</td>
                                <td className="px-2 py-1 text-red-700">{err.error}</td>
                              </tr>
                            ))}
                          </tbody>
                        </ResponsiveTable>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={handleFileUpload}
                  disabled={!selectedFile || uploadLoading}
                  className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadLoading ? 'Uploading...' : 'Upload'}
                </button>
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="btn-ghost flex-1"
                >
                  {uploadResult ? 'Close' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Users Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteAllModal}
        onClose={() => setShowDeleteAllModal(false)}
        onConfirm={handleDeleteAllUsers}
        title="Delete All Users"
        message={`This action will permanently delete ALL ${Math.max(meta.totalUsers, users.length)} users from the database — all user records, their data (names, districts, units, etc.) and associated submissions. This cannot be undone.`}
        confirmText="Yes, Delete All Users"
        variant="danger"
        isLoading={deleteAllLoading}
      />

      {/* Add Member Modal */}
      <MemberFormModal
        isOpen={showAddMemberModal}
        onClose={() => setShowAddMemberModal(false)}
        editUser={null}
        onSaved={() => {
          fetchUsers(1) // newest members sort to the top of page 1
          fetchMeta()
        }}
      />

      {/* Unit Admin Profile Modal */}
      <UnitAdminProfileModal
        unitAdminId={selectedUnitAdminId}
        isOpen={showProfileModal}
        onClose={() => {
          setShowProfileModal(false)
          setSelectedUnitAdminId(null)
        }}
      />

      {/* User Profile Modal */}
      <UserProfileModal
        userId={selectedUserId}
        isOpen={showUserProfileModal}
        onClose={() => {
          setShowUserProfileModal(false)
          setSelectedUserId(null)
        }}
      />

      {/* Delete User Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteUserModal.isOpen}
        onClose={() => setDeleteUserModal({ isOpen: false, userId: null, userName: null })}
        onConfirm={confirmDeleteUser}
        title="Delete User"
        message={`Are you sure you want to delete user "${deleteUserModal.userName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Transfer User Modal */}
      {transferModal.isOpen && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between gap-2 mb-4">
              <h3 className="min-w-0 flex-1 truncate text-lg font-semibold text-gray-900">
                Transfer: {transferModal.user?.name}
              </h3>
              <button onClick={() => setTransferModal({ isOpen: false, user: null })} className="shrink-0 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Mode tabs */}
            <div className="flex border-b mb-4">
              <button
                onClick={() => { setTransferMode('location'); setTransferError('') }}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${transferMode === 'location' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                Location Transfer
              </button>
              <button
                onClick={() => { setTransferMode('abroad'); setTransferError('') }}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${transferMode === 'abroad' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                Transfer to Abroad
              </button>
            </div>

            {transferMode === 'location' && (
              <LocationSelects value={transferLoc} onChange={setTransferLoc} required />
            )}

            {transferMode === 'abroad' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <select
                    value={transferAbroadCountry}
                    onChange={(e) => handleTransferAbroadCountryChange(e.target.value)}
                    className="form-select text-[13px] sm:text-sm"
                  >
                    <option value="">Select country</option>
                    {abroadCountries.map((c) => (
                      <option key={c._id} value={c._id}>{c.title}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Area <span className="text-gray-400 font-normal">(optional)</span></label>
                  <select
                    value={transferAbroadArea}
                    onChange={(e) => handleTransferAbroadAreaChange(e.target.value)}
                    disabled={!transferAbroadCountry || abroadAreas.length === 0}
                    className="form-select text-[13px] sm:text-sm"
                  >
                    <option value="">{!transferAbroadCountry ? 'Select country first' : abroadAreas.length === 0 ? 'No areas available' : 'Select area'}</option>
                    {abroadAreas.map((a) => (
                      <option key={a._id} value={a._id}>{a.title}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit <span className="text-gray-400 font-normal">(optional)</span></label>
                  <select
                    value={transferAbroadUnit}
                    onChange={(e) => setTransferAbroadUnit(e.target.value)}
                    disabled={!transferAbroadArea || abroadUnits.length === 0}
                    className="form-select text-[13px] sm:text-sm"
                  >
                    <option value="">{!transferAbroadArea ? 'Select area first' : abroadUnits.length === 0 ? 'No units available' : 'Select unit'}</option>
                    {abroadUnits.map((u) => (
                      <option key={u._id} value={u._id}>{u.title}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {transferError && <p className="text-sm text-red-600 mt-2">{transferError}</p>}

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setTransferModal({ isOpen: false, user: null })}
                disabled={transferWorking}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmTransfer}
                disabled={transferWorking}
                className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                {transferWorking ? 'Transferring…' : 'Confirm Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserManagement
